import { useState, useEffect } from 'react';
/* eslint-disable */
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    Typography,
    Box,
    IconButton,
    CircularProgress
} from '@mui/material';
import { EditOutlined, CloseOutlined, HistoryOutlined, CancelOutlined, CheckCircleOutlined } from '@mui/icons-material';
import UploadField from './UploadField';
import { updateEmployeeStatus } from 'utils/API/Mis Apis/HrPortelApis';
import { handleCatchError } from 'utils/CatchHandler';
import { globalNotifySuccess } from 'views/utilities/AlertToast';

const UpdateStatusModal = ({ open, handleClose, employee, type, onRefresh }) => {
    const [status, setStatus] = useState('');
    const [bgvRemark, setBgvRemark] = useState('');
    const [bgvDocument, setBgvDocument] = useState(null);
    const [bgvDocumentName, setBgvDocumentName] = useState('');
    const [bgvDocumentPreview, setBgvDocumentPreview] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Size validation (1MB = 1 * 1024 * 1024 bytes)
        const maxSize = 1 * 1024 * 1024;
        if (file.size > maxSize) {
            alert('File size should not exceed 1MB');
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            setBgvDocument(reader.result);
            setBgvDocumentPreview(reader.result);
            setBgvDocumentName(file.name);
        };
        reader.readAsDataURL(file);
    };

    const bgvOptions = [
        { value: 'Verified', label: 'Verified' },
        { value: 'Not Verified', label: 'Not Verified' },
        { value: 'On Progress', label: 'On Progress' },
        { value: 'Rejected', label: 'Rejected' }
    ];

    const trainingOptions = [
        { value: 'Completed', label: 'Completed' },
        { value: 'Not Completed', label: 'Not Completed' },
        { value: 'Ongoing', label: 'Ongoing' }
    ];

    const options = type === 'bgv' ? bgvOptions : trainingOptions;

    useEffect(() => {
        if (open && employee) {
            setStatus(type === 'bgv' ? employee.bgv_status : employee.training_status);
            if (type === 'bgv') {
                setBgvRemark(employee.bgv_remarks || '');
                setBgvDocument(employee.bgv_document || null);
                setBgvDocumentPreview(employee.bgv_document || null);
                setBgvDocumentName(employee.bgv_document ? 'Existing Document' : '');
            }
        }
    }, [open, employee, type]);

    const handleSubmit = async () => {
        if (!status) return;
        setLoading(true);

        const payload = {
            employee_id: employee.id,
            [type === 'bgv' ? 'bgv_status' : 'training_status']: status,
            ...(type === 'bgv' && {
                bgv_remarks: bgvRemark,
                bgv_document: bgvDocument
            })
        };

        try {
            const response = await updateEmployeeStatus(payload);
            if (response?.data?.success) {
                globalNotifySuccess(response.data.message || 'Status updated successfully');
                if (onRefresh) onRefresh();
                handleClose();
            }
        } catch (error) {
            handleCatchError(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 3, boxShadow: '0 20px 40px rgba(0,0,0,0.1)', overflow: 'hidden' }
            }}
        >
            <DialogTitle sx={{ p: 0, mb: 2 }}>
                <Box
                    sx={{
                        background: 'linear-gradient(135deg, #1e3a8a 0%, #1976d2 100%)',
                        px: 3,
                        py: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        color: '#fff'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 1.5,
                                bgcolor: 'rgba(255,255,255,0.15)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <EditOutlined sx={{ color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                                Update {type === 'bgv' ? 'BGV' : 'Training'} Status
                            </Typography>
                            {employee && (
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                    {employee.name} • {employee.id}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    <IconButton
                        size="small"
                        onClick={handleClose}
                        sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}
                    >
                        <CloseOutlined fontSize="small" />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ pt: 1, pb: 3 }}>
                <TextField
                    select
                    fullWidth
                    label={`Select ${type === 'bgv' ? 'BGV' : 'Training'} Status`}
                    value={status || ''}
                    onChange={(e) => setStatus(e.target.value)}
                    variant="outlined"
                    sx={{
                        mt: 2.5,
                        '& .MuiOutlinedInput-root': { borderRadius: 2 }
                    }}
                >
                    {options.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </TextField>

                {type === 'bgv' && status === 'Rejected' && (
                    <TextField
                        fullWidth
                        label="Rejected Remark"
                        value={bgvRemark}
                        onChange={(e) => setBgvRemark(e.target.value)}
                        multiline
                        rows={2}
                        sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                )}

                {type === 'bgv' && status === 'Verified' && (
                    <Box sx={{ mt: 2 }}>
                        <UploadField
                            label="BGV Document"
                            preview={bgvDocumentPreview}
                            fileName={bgvDocumentName}
                            onChange={handleFileChange}
                        />
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3, gap: 1.5 }}>
                <Button onClick={handleClose} sx={{ color: 'text.secondary', fontWeight: 600 }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading}
                    sx={{
                        px: 4,
                        py: 1,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 700,
                        background: 'linear-gradient(135deg, #1e3a8a 0%, #1976d2 100%)',
                        '&:hover': { background: 'linear-gradient(135deg, #1e40af 0%, #1d4ed8 100%)' }
                    }}
                >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Update Status'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

UpdateStatusModal.propTypes = {
    open: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    employee: PropTypes.object,
    type: PropTypes.oneOf(['bgv', 'training']).isRequired,
    onRefresh: PropTypes.func
};

export default UpdateStatusModal;
